import {
  cricketNumbers,
  currentLegRound,
  displayPlayerOrder,
  formatVisit,
  isCheckoutFrozen,
  isTeamFormat,
  lastVisitForPlayer,
  playingTeamCount,
  sideIndices,
  sideLabel,
  sideOf,
  sidePlayerNames,
  teamClosedNumber,
  teammateIndices,
  type MatchState,
  type PlayerLegState,
} from "@shared/index";
import { boardNumberLabel, cricketGameLabel } from "../lib/labels";
import LivePlayerName from "./LivePlayerName";

interface Props {
  state: MatchState;
  highscoreBests?: Record<string, number>;
}

function PlayerCard({
  state,
  index,
  compact,
  highscoreBests,
}: {
  state: MatchState;
  index: number;
  compact?: boolean;
  highscoreBests?: Record<string, number>;
}) {
  const player = state.config.players[index];
  const leg = state.currentLeg?.players[index];
  if (!player || !leg) return null;
  const league = state.config.teamPlay === "league";
  const mateIdx = league ? teammateIndices(index) : [index];
  const active = mateIdx.includes(state.currentLeg?.currentPlayerIndex ?? -1) && state.status === "playing";
  const last = lastVisitForPlayer(state, player.id) ?? (league
    ? lastVisitForPlayer(state, state.config.players[mateIdx.find((i) => i !== index)!]?.id ?? "")
    : undefined);
  const game = state.config.gameType;
  const knocked = (state.currentLeg?.knockedPlayerIds ?? []).includes(player.id);
  const frozen =
    game === "x01" &&
    state.config.teamPlay === "team" &&
    isCheckoutFrozen(state.currentLeg, index);

  return (
    <article
      className={`relative overflow-hidden rounded-3xl border px-4 py-3 transition ${
        knocked
          ? "border-crimson bg-crimson/15"
          : active
            ? "border-amber-glow bg-amber-glow/10 shadow-glow"
            : "border-white/10 bg-ink-800/80"
      } ${leg.eliminated ? "opacity-45" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {league ? (
            <>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-glow/80">
                {sideLabel(state.config, sideOf(index))}
              </p>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                {mateIdx.map((i) => state.config.players[i]?.name).filter(Boolean).join(", ")}
              </p>
            </>
          ) : (
            <LivePlayerName
              player={player}
              nameClassName="text-xs font-bold uppercase tracking-[0.2em] text-white/50"
              teamClassName="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-glow/80"
            />
          )}
          <Headline
            game={game}
            leg={leg}
            compact={compact}
            knocked={knocked}
            hideScore={game === "cricket" && state.config.cricket.scoring === "noScore"}
            best={player.id ? highscoreBests?.[player.id] : undefined}
          />
          {frozen && (
            <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-crimson">Ausmachen gesperrt</p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Stand</p>
          <StandingsFigures state={state} index={index} compact />
        </div>
      </div>
      {knocked && (
        <p className="mt-2 text-xs font-black uppercase tracking-[0.2em] text-crimson">Auf 0!</p>
      )}
      {game === "cricket" && !compact && <CricketMarks state={state} player={leg} />}
      <p className="mt-2 min-h-[1.25rem] text-sm text-white/45">
        {last
          ? `Letzte Aufnahme: ${last.bust ? "Bust" : last.kind === "total" ? last.total : formatVisit(last.darts)}${
              last.shanghai ? " · Shanghai!" : ""
            }`
          : " "}
      </p>
    </article>
  );
}

function TeamBlock({ state, side }: { state: MatchState; side: number }) {
  const idxs = sideIndices(side);
  const lead = idxs[0] ?? 0;
  const league = state.config.teamPlay === "league";
  const current = state.currentLeg?.currentPlayerIndex ?? -1;
  const active = idxs.includes(current) && state.status === "playing";
  const game = state.config.gameType;
  const hideScore = game === "cricket" && state.config.cricket.scoring === "noScore";
  const knockedIds = state.currentLeg?.knockedPlayerIds ?? [];
  const knocked = idxs.some((i) => knockedIds.includes(state.config.players[i]?.id ?? ""));
  const lastFromThrower = lastVisitForPlayer(state, state.config.players[current]?.id ?? "");
  const last =
    lastFromThrower ??
    idxs.map((i) => lastVisitForPlayer(state, state.config.players[i]?.id ?? "")).find(Boolean);
  const leadLeg = state.currentLeg?.players[lead];
  if (!leadLeg) return null;

  return (
    <article
      className={`relative overflow-hidden rounded-3xl border px-4 py-3 transition ${
        knocked
          ? "border-crimson bg-crimson/15"
          : active
            ? "border-amber-glow bg-amber-glow/10 shadow-glow"
            : "border-white/10 bg-ink-800/80"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-glow/80">
            {sideLabel(state.config, side)}
          </p>
          {league ? (
            <>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                {sidePlayerNames(state.config, side)}
              </p>
              <Headline game={game} leg={leadLeg} knocked={knocked} hideScore={hideScore} />
            </>
          ) : (
            <div className="mt-1 grid grid-cols-2 gap-3">
              {idxs.map((index) => {
                const player = state.config.players[index];
                const leg = state.currentLeg?.players[index];
                if (!player || !leg) return null;
                const throwing = index === current && state.status === "playing";
                const frozen =
                  game === "x01" &&
                  state.config.teamPlay === "team" &&
                  isCheckoutFrozen(state.currentLeg, index);
                return (
                  <div key={player.id || index} className={throwing ? "rounded-2xl bg-amber-glow/10 px-2 py-1" : "px-2 py-1"}>
                    <p
                      className={`truncate text-xs font-bold uppercase tracking-[0.16em] ${
                        throwing ? "text-amber-glow" : "text-white/70"
                      }`}
                    >
                      {player.name}
                    </p>
                    <Headline game={game} leg={leg} compact knocked={knockedIds.includes(player.id)} hideScore={hideScore} />
                    {frozen && (
                      <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-crimson">
                        Ausmachen gesperrt
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Stand</p>
          <StandingsFigures state={state} index={lead} compact />
        </div>
      </div>
      {knocked && (
        <p className="mt-2 text-xs font-black uppercase tracking-[0.2em] text-crimson">Auf 0!</p>
      )}
      <p className="mt-2 min-h-[1.25rem] text-sm text-white/45">
        {last
          ? `Letzte Aufnahme: ${last.bust ? "Bust" : last.kind === "total" ? last.total : formatVisit(last.darts)}${
              last.shanghai ? " · Shanghai!" : ""
            }`
          : " "}
      </p>
    </article>
  );
}

function Headline({
  game,
  leg,
  compact,
  knocked,
  hideScore,
  best,
}: {
  game: MatchState["config"]["gameType"];
  leg: PlayerLegState;
  compact?: boolean;
  knocked?: boolean;
  hideScore?: boolean;
  best?: number;
}) {
  if (game === "x01") {
    return (
      <p className={`num-display text-amber-glow ${compact ? "text-5xl" : "kiosk-score"}`}>
        {leg.remaining}
      </p>
    );
  }
  if (game === "cricket") {
    if (hideScore) return null;
    return <p className="num-display text-5xl text-amber-glow sm:text-7xl">{leg.cricketScore}</p>;
  }
  if (game === "elimination") {
    return (
      <p className={`num-display ${knocked ? "text-crimson" : "text-amber-glow"} ${compact ? "text-5xl" : "text-5xl sm:text-7xl"}`}>
        {leg.remaining}
      </p>
    );
  }
  if (game === "clock") {
    const t = leg.clockFinished ? "Fertig" : boardNumberLabel(leg.nextTarget);
    return <p className="num-display text-5xl text-amber-glow sm:text-7xl">{t}</p>;
  }
  if (game === "highscore") {
    return (
      <div>
        <p className="num-display text-5xl text-amber-glow sm:text-7xl">{leg.highscoreScore ?? 0}</p>
        {best != null && best > 0 ? (
          <p className="mt-1 text-sm font-bold uppercase tracking-widest text-slate-400">Rekord {best}</p>
        ) : null}
      </div>
    );
  }
  return (
    <div>
      <p className="num-display text-5xl text-amber-glow sm:text-7xl">{leg.shanghaiScore}</p>
      <p className="mt-1 text-sm font-bold uppercase tracking-widest text-slate-400">Ziel {boardNumberLabel(leg.nextTarget)}</p>
    </div>
  );
}

function CricketMarkGlyph({ marks }: { marks: number }) {
  if (marks <= 0) return null;
  return (
    <svg className="cricket-glyph" viewBox="0 0 32 32" aria-hidden>
      {marks >= 3 ? <circle cx="16" cy="16" r="11.6" /> : null}
      {marks >= 1 ? <line x1="10" y1="22" x2="22" y2="10" /> : null}
      {marks >= 2 ? <line x1="10" y1="10" x2="22" y2="22" /> : null}
    </svg>
  );
}

function CricketMarks({ state, player }: { state: MatchState; player: PlayerLegState }) {
  const targets = cricketNumbers(state.config.cricket);
  return (
    <div className="mt-3 grid grid-cols-7 gap-1 sm:grid-cols-7">
      {targets.map((n) => {
        const marks = player.marks[String(n)] ?? 0;
        return (
          <div
            key={n}
            className={`rounded-xl px-1 py-2 text-center ${
              marks >= 3 ? "bg-amber-glow/20 text-amber-glow" : "bg-black/30 text-white/70"
            }`}
          >
            <div className="text-[10px] font-bold uppercase">{n === 25 ? "Bull" : n}</div>
            <div className="mt-1 flex justify-center">
              <CricketMarkGlyph marks={marks} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CricketBoard({ state }: { state: MatchState }) {
  const targets = cricketNumbers(state.config.cricket);
  const players = state.config.players ?? [];
  const league = state.config.teamPlay === "league" && players.length >= 4;
  const teamHeaders = state.config.teamPlay === "team" && players.length >= 4;
  const columnIdxs = league
    ? Array.from({ length: playingTeamCount(state.config) }, (_, side) => sideIndices(side)[0])
    : displayPlayerOrder(state.config);
  const n = Math.max(1, columnIdxs.length);
  const scoring = state.config.cricket.scoring ?? "normal";
  const showScore = scoring !== "noScore";
  const active = state.currentLeg?.currentPlayerIndex ?? 0;

  return (
    <div className="cricket-board flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border border-white/10 bg-ink-800/80">
      <div
        className="grid min-h-0 flex-1"
        style={{
          gridTemplateColumns: `minmax(3.2rem, 0.7fr) repeat(${n}, minmax(0, 1fr))`,
          gridTemplateRows: `${teamHeaders ? "auto " : ""}auto repeat(${targets.length}, 1px minmax(0, 1fr))`,
        }}
      >
        {teamHeaders ? (
          <>
            <div />
            {Array.from({ length: playingTeamCount(state.config) }, (_, side) => {
              const throwing = sideIndices(side).includes(active) && state.status === "playing";
              return (
                <div
                  key={`team-${side}`}
                  className={`col-span-2 truncate border-l border-white/20 px-1 pt-2 text-center text-[11px] font-bold uppercase tracking-[0.16em] sm:text-xs ${
                    throwing ? "bg-amber-glow/15 text-amber-glow" : "text-amber-glow/80"
                  }`}
                >
                  {sideLabel(state.config, side)}
                </div>
              );
            })}
          </>
        ) : null}
        <div className="flex items-end justify-center px-1 pb-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
          {cricketGameLabel(scoring)}
        </div>
        {columnIdxs.map((index) => {
          const player = players[index];
          const leg = state.currentLeg?.players[index];
          const throwing = league
            ? teammateIndices(index).includes(active) && state.status === "playing"
            : index === active && state.status === "playing";
          const last = player ? lastVisitForPlayer(state, player.id) : undefined;
          return (
            <div
              key={player?.id || index}
              className={`flex min-w-0 flex-col items-center justify-end border-l border-white/20 px-1 py-2 text-center ${
                throwing ? "bg-amber-glow/15" : ""
              }`}
            >
              {league ? (
                <>
                  <div className={`truncate text-[11px] font-bold uppercase tracking-[0.16em] sm:text-xs ${
                    throwing ? "text-amber-glow" : "text-slate-300"
                  }`}>
                    {sideLabel(state.config, sideOf(index))}
                  </div>
                  <div className="truncate text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">
                    {sidePlayerNames(state.config, sideOf(index))}
                  </div>
                </>
              ) : player ? (
                <div
                  className={`truncate text-[11px] font-bold uppercase tracking-[0.16em] sm:text-xs ${
                    throwing ? "text-amber-glow" : "text-slate-300"
                  }`}
                >
                  {player.name}
                </div>
              ) : null}
              {showScore && (
                <div className="num-display cricket-score mt-1 text-amber-glow">{leg?.cricketScore ?? 0}</div>
              )}
              <div className="mt-0.5 min-h-[1rem] truncate text-[10px] text-white/40">
                {last ? formatVisit(last.darts) : " "}
              </div>
            </div>
          );
        })}
        {targets.map((target) => (
          <CricketTargetRow
            key={target}
            state={state}
            target={target}
            columnIdxs={columnIdxs}
            active={active}
          />
        ))}
      </div>
    </div>
  );
}

function CricketTargetRow({
  state,
  target,
  columnIdxs,
  active,
}: {
  state: MatchState;
  target: number;
  columnIdxs: number[];
  active: number;
}) {
  const allClosed = columnIdxs
    .map((index) => (state.currentLeg?.players[index]?.marks[String(target)] ?? 0) >= 3)
    .every(Boolean);
  const strike =
    "relative after:pointer-events-none after:absolute after:inset-x-0 after:top-1/2 after:h-[2px] after:-translate-y-1/2 after:bg-white/45 after:content-['']";
  const teamMode = state.config.teamPlay === "team";

  return (
    <>
      <div className="col-span-full h-px bg-white/40" />
      <div
        className={`cricket-num flex items-center justify-center font-display ${
          allClosed ? `text-slate-500 ${strike}` : "text-white"
        }`}
      >
        {target === 25 ? "Bull" : target}
      </div>
      {columnIdxs.map((index) => {
        const marks = state.currentLeg?.players[index]?.marks[String(target)] ?? 0;
        const closed = marks >= 3;
        const throwing = index === active && state.status === "playing";
        const waitingOnPartner = teamMode && closed && !teamClosedNumber(state.currentLeg, index, String(target));
        return (
          <div
            key={`${target}-${index}`}
            className={`cricket-mark ${
              throwing && !allClosed ? "bg-amber-glow/10" : ""
            } ${allClosed ? `text-slate-500 ${strike}` : closed ? "text-amber-glow" : "text-white/80"} ${
              waitingOnPartner ? "opacity-50" : ""
            }`}
            title={waitingOnPartner ? "Punkte erst wenn der Partner geschlossen hat" : undefined}
          >
            <CricketMarkGlyph marks={marks} />
          </div>
        );
      })}
    </>
  );
}

function StandingsFigures({
  state,
  index,
  compact,
}: {
  state: MatchState;
  index: number;
  compact?: boolean;
}) {
  const game = state.config.gameType;
  const num = compact ? "text-2xl sm:text-3xl" : "standings-num";
  if (game === "highscore") {
    const score = state.currentLeg?.players[index]?.highscoreScore ?? 0;
    return (
      <p className="leading-none">
        <span className={`num-display text-amber-glow ${num}`}>{score}</span>
        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Pkt</span>
      </p>
    );
  }
  const showSets = state.config.setsToWin > 1;
  const sets = state.setsWon[index] ?? 0;
  const legs = state.legsWon[index] ?? 0;
  if (showSets) {
    return (
      <p className="leading-none">
        <span className={`num-display text-white ${num}`}>{sets}</span>
        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Sätze</span>
        <span className="mx-1.5 text-slate-600">·</span>
        <span className={`num-display text-amber-glow ${num}`}>{legs}</span>
        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Legs</span>
      </p>
    );
  }
  return (
    <p className="leading-none">
      <span className={`num-display text-amber-glow ${num}`}>{legs}</span>
      <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Legs</span>
    </p>
  );
}

/** Always-visible match score: legs (and sets) for every player. */
export function StandingsStrip({ state, className = "" }: Props & { className?: string }) {
  const players = state.config.players ?? [];
  const teamish = isTeamFormat(state.config) && players.length >= 4;
  const cells = teamish
    ? Array.from({ length: playingTeamCount(state.config) }, (_, side) => ({
        key: `side-${side}`,
        label: sideLabel(state.config, side),
        index: sideIndices(side)[0],
        sub: sidePlayerNames(state.config, side),
      }))
    : displayPlayerOrder(state.config).map((index) => {
        const player = players[index];
        return {
          key: player?.id || String(index),
          label: player?.name ?? `Spieler ${index + 1}`,
          index,
          sub: player?.teamName ?? "",
          player,
        };
      });
  const n = Math.max(1, cells.length);
  return (
    <div className={`border-b border-white/10 bg-black/25 px-3 py-2 sm:px-4 landscape:lg:col-span-2 ${className}`}>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${Math.min(n, 4)}, minmax(0, 1fr))` }}
      >
        {cells.map((cell) => (
          <div key={cell.key} className="min-w-0 rounded-2xl bg-ink-800 px-3 py-2 text-center">
            {"player" in cell && cell.player ? (
              <LivePlayerName
                player={cell.player}
                nameClassName="truncate text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400"
                teamClassName="truncate text-[10px] font-bold uppercase tracking-[0.16em] text-amber-glow/80"
              />
            ) : (
              <>
                <div className="truncate text-[11px] font-bold uppercase tracking-[0.18em] text-amber-glow/80">
                  {cell.label}
                </div>
                <div className="truncate text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  {cell.sub}
                </div>
              </>
            )}
            <div className="mt-1">
              <StandingsFigures state={state} index={cell.index} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Scoreboard({ state, highscoreBests }: Props) {
  const round = currentLegRound(state);
  if (state.config.gameType === "cricket") {
    return (
      <div className="flex min-h-0 flex-col overflow-hidden p-2 sm:p-3">
        <CricketBoard state={state} />
      </div>
    );
  }
  return (
    <div className="scroll-thin flex min-h-0 flex-col gap-3 overflow-auto p-3 sm:p-4">
      <div className="flex items-baseline justify-center gap-3 rounded-2xl bg-ink-800 px-4 py-3">
        <span className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Runde</span>
        <span className="font-display text-5xl leading-none text-amber-glow sm:text-6xl">{round}</span>
      </div>
      {isTeamFormat(state.config) && (state.config.players ?? []).length >= 4
        ? Array.from({ length: playingTeamCount(state.config) }, (_, side) => (
            <TeamBlock key={`side-${side}`} state={state} side={side} />
          ))
        : displayPlayerOrder(state.config).map((i) => (
            <PlayerCard
              key={state.config.players[i]?.id ?? i}
              state={state}
              index={i}
              highscoreBests={highscoreBests}
            />
          ))}
    </div>
  );
}
